import { useEffect, useState } from 'react';
import { Camera, ExternalLink, Maximize2, Upload, FileText, ShieldCheck } from 'lucide-react';
import type { LogbookAttachment } from '../../types/logbook';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { LogbookPhotoPicker } from './LogbookPhotoPicker';
import { ImageLightbox } from '../ui/ImageLightbox';
import { formatDateTime } from '../../utils/trips';
import { mensajeErrorNovedad } from '../../utils/logbook';
import { formatearPeso } from '../../utils/format';

interface Props {
  attachments: LogbookAttachment[];
  isLoading: boolean;
  error: Error | null;
  subiendo: boolean;
  progreso: { actual: number; total: number } | null;
  /** Capacidad temporal sobre una foto concreta. Se pide al abrirla. */
  urlDeDescarga: (attachmentId: string) => Promise<string>;
  /** Ausente = solo lectura: el rol no puede adjuntar, o venció la ventana de edición. */
  onSubir?: (files: File[]) => Promise<unknown>;
  /** Cuántas caben todavía. */
  quedanCupos: number;
}

/**
 * FE-30 — las fotos de una novedad.
 *
 * **Una galería, no un "vigente + anteriores".** Esa es la diferencia con
 * `ReceiptViewer`, del que copia casi todo lo demás: el recibo de un gasto es
 * UN documento que se vuelve a fotografiar, y una novedad tiene fotos de cosas
 * distintas —el guardabarros, la placa del otro carro, el croquis—.
 *
 * ⚠️ **La URL firmada se pide al ABRIR cada foto, no al montar la galería.** Es
 * una capacidad temporal sobre un objeto concreto: pedir una por miniatura
 * sería N+1 y además caducaría mientras el usuario mira la pantalla. Por eso
 * las miniaturas se resuelven perezosamente y en paralelo solo cuando la
 * galería ya está en pantalla.
 *
 * **Las fotos se abren DENTRO de la app**, en `ImageLightbox`. Al principio se
 * abrían con `window.open`, y el dueño lo midió en su teléfono (2026-09-22):
 * en un móvil eso te saca de la aplicación, y volver no es un clic sino salir
 * y buscarla otra vez.
 *
 * ⚠️ **Un PDF sigue abriéndose en una pestaña**, y no es una excepción por
 * pereza: incrustarlo con una URL firmada de GCS depende de cabeceras del
 * bucket que no controlamos, y falla en blanco. Un adjunto puede ser el
 * comparendo escaneado de una sanción, así que el caso existe.
 *
 * **No hay botón de borrar, y no es un olvido:** la bitácora no borra (ADR-007)
 * y el GRANT de la tabla no tiene `DELETE`. Si apareciera ese botón, el
 * servidor lo rechazaría — mejor no prometerlo.
 */
export function LogbookAttachmentsViewer({
  attachments, isLoading, error, subiendo, progreso, urlDeDescarga, onSubir, quedanCupos,
}: Props) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [nuevas, setNuevas] = useState<File[]>([]);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);
  const [mirando, setMirando] = useState<number | null>(null);

  // Solo las imágenes entran al visor: un PDF no se puede incrustar (ver la
  // cabecera). El índice que guarda `mirando` es de ESTA lista, no de
  // `attachments`, para que pasar de foto no se tropiece con los PDF.
  const fotos = attachments
    .filter(a => a.contentType !== 'application/pdf')
    .map(a => ({
      id: a.id,
      url: urls[a.id] ?? '',
      pie: `${formatDateTime(a.uploadedAt)} · ${formatearPeso(a.sizeBytes)}`,
    }))
    .filter(f => f.url !== '');

  // Las miniaturas de las IMÁGENES se resuelven al montar la galería; los PDF
  // no tienen miniatura y esperan a que alguien los abra.
  useEffect(() => {
    let cancelled = false;
    const imagenes = attachments.filter(a => a.contentType !== 'application/pdf' && !urls[a.id]);
    if (imagenes.length === 0) return;

    Promise.all(imagenes.map(async a => {
      try {
        return [a.id, await urlDeDescarga(a.id)] as const;
      } catch {
        return null;
      }
    })).then(res => {
      if (cancelled) return;
      const nuevos: Record<string, string> = {};
      for (const par of res) if (par) nuevos[par[0]] = par[1];
      if (Object.keys(nuevos).length > 0) setUrls(prev => ({ ...prev, ...nuevos }));
    });

    return () => { cancelled = true; };
    // `urls` a propósito fuera: incluirlo reejecutaría el efecto con cada
    // resolución y pediría URLs en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, urlDeDescarga]);

  const abrir = async (a: LogbookAttachment) => {
    try {
      const url = urls[a.id] ?? await urlDeDescarga(a.id);

      // El PDF sale a una pestaña; la foto se mira aquí dentro.
      if (a.contentType === 'application/pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      // Si la miniatura aún no había resuelto su URL, se guarda ahora: sin
      // esto el visor abriría con `src` vacío justo en el caso lento, que es
      // el único en el que se nota.
      if (!urls[a.id]) setUrls(prev => ({ ...prev, [a.id]: url }));

      const i = fotos.findIndex(f => f.id === a.id);
      setMirando(i >= 0 ? i : 0);
    } catch (err) {
      setErrorSubida(mensajeErrorNovedad(err, 'adjuntar'));
    }
  };

  const subir = async () => {
    if (!onSubir || nuevas.length === 0) return;
    setErrorSubida(null);
    try {
      await onSubir(nuevas);
      setNuevas([]);
    } catch (err) {
      setErrorSubida(mensajeErrorNovedad(err, 'adjuntar'));
    }
  };

  if (isLoading) {
    return <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>;
  }

  return (
    <div>
      <p style={{ margin: '0 0 0.5rem', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Camera size={14} /> Fotos {attachments.length > 0 && `(${attachments.length})`}
      </p>

      {error && (
        <p style={{ margin: '0 0 0.5rem', fontSize: '12px', color: '#EF4444' }}>
          No se pudieron cargar las fotos de esta novedad.
        </p>
      )}

      {attachments.length === 0 && !error && (
        <p style={{ margin: '0 0 0.625rem', fontSize: '12px', color: 'var(--text-muted)' }}>
          Sin fotos.
        </p>
      )}

      {attachments.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
          gap: '0.5rem', marginBottom: '0.75rem',
        }}>
          {attachments.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => abrir(a)}
              title={`${formatDateTime(a.uploadedAt)} · ${formatearPeso(a.sizeBytes)}`}
              style={{
                position: 'relative', aspectRatio: '1', padding: 0, overflow: 'hidden',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                background: 'var(--bg-raised)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {a.contentType === 'application/pdf' ? (
                <FileText size={26} style={{ color: 'var(--text-secondary)' }} />
              ) : urls[a.id] ? (
                <img src={urls[a.id]} alt={`Foto del ${formatDateTime(a.uploadedAt)}`}
                     style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Spinner />
              )}
              {/* El icono dice a dónde va: la foto se amplía aquí dentro y el
                  PDF sí sale a otra pestaña. Cuando las dos cosas abrían
                  fuera, el ↗ era correcto; ahora sería una mentira pequeña. */}
              {a.contentType === 'application/pdf' ? (
                <ExternalLink
                  size={12}
                  style={{
                    position: 'absolute', bottom: '4px', right: '4px', color: '#FFF',
                    background: 'rgba(0,0,0,0.6)', borderRadius: '3px', padding: '2px',
                  }}
                />
              ) : (
                <Maximize2
                  size={12}
                  style={{
                    position: 'absolute', bottom: '4px', right: '4px', color: '#FFF',
                    background: 'rgba(0,0,0,0.6)', borderRadius: '3px', padding: '2px',
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* El hash es lo único que prueba que el archivo descargado es el que se
          subió. Decirlo importa: un adjunto NO es evidencia forense —lo sube
          una persona y vale lo que valga su palabra—, y conviene que quien lo
          mire sepa exactamente qué garantía tiene y cuál no. */}
      {attachments.length > 0 && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
          <ShieldCheck size={12} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span>
            Cada foto guarda su huella SHA-256, así que se puede probar que no cambió desde que
            se subió. No se pueden borrar.
          </span>
        </p>
      )}

      {onSubir && quedanCupos > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
          <LogbookPhotoPicker files={nuevas} onChange={setNuevas} disabled={subiendo} cupo={quedanCupos} />
          {nuevas.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={subir}
              isLoading={subiendo}
              style={{ width: '100%', marginTop: '0.625rem', gap: '0.5rem' }}
            >
              <Upload size={14} />
              {subiendo && progreso
                ? `Subiendo ${progreso.actual} de ${progreso.total}…`
                : `Adjuntar ${nuevas.length === 1 ? 'la foto' : `las ${nuevas.length} fotos`}`}
            </Button>
          )}
        </div>
      )}

      {errorSubida && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '12px', color: '#EF4444' }}>{errorSubida}</p>
      )}

      <ImageLightbox
        imagenes={fotos}
        indice={mirando}
        onClose={() => setMirando(null)}
        onCambiar={setMirando}
      />
    </div>
  );
}
